import AddIcon from "@mui/icons-material/Add";
import { styled } from "@mui/material/styles";
import { black } from "designSystem/styles/color";
import { ButtonEnum } from "designSystem/types/button-types";
import { ModalDialogProps } from "designSystem/components/dialog/ModalDialog";
import {
	Box,
	Button,
	ConfirmDialog,
	FlexColumn,
	FlexRowCenter,
	FormControlLabel,
	Message,
	PopupDialog,
	SaveDialog,
	SingleSelect,
	Text,
	TextField
} from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { ControllerProps } from "csi/middleware/common";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, SyntheticEvent, useEffect, useState } from "react";
import { USE_CUSTOM, USE_TEMPLATE } from "../ProofService";
import ProofingStage from "./ProofingStage";
import { useProofingStore } from "./ProofingStore";
import { ComponentItem } from "designSystem/types/component-item-types";
import { getDialogZIndex } from "csi/utility/htmlUtility";

const PREFIX = "ProofOperationDialog";

const classes = {
	textFieldContainer: `${PREFIX}textFieldContainer`,
	textInput: `${PREFIX}textInput`,
};

const RootBox = styled(Box)(({
	theme,
}) => ({
	width: 700,

	[`& .${classes.textFieldContainer}`]: {
		justifyContent: "flex-end",
		width: 416,
	},

	[`& .${classes.textInput}`]: {
		width: 352,
	},
}));

const StyledSingleSelect = styled(SingleSelect)(({ theme }) => ({
	height: 26,
	width: 352,
}));

export interface ProofOperationDialogProps extends ModalDialogProps, ControllerProps {
	isEditOperation?: boolean;
}

const ProofOperationDialog: FunctionComponent<ProofOperationDialogProps> = observer((props) => {
	const loc = useLocale();

	const USE_TEMPLATE_INDEX = 0;
	const USE_CUSTOM_INDEX = 1;

	const store = useProofingStore();
	const {
		activeTemplates,
		allowAddStage,
		buildRequest,
		formError,
		getPreviousDocRevisionWithProof,
		hasErrorOnStage,
		isCustomTemplateEdited,
		isSelectProofOwnerDialogOpen,
		onChangeProofOwnerFromLegacy,
		onCloseSelectNewProofOwnerFromLegacy,
		onSaveSelectNewProofOwnerFromLegacy,
		populateForm,
		proofAdminUsers,
		proofOwnerValue,
		readyToShowStages,
		selectedWorkflow,
		selectedTemplate,
		stages,
		validateForm,
	} = store;

	const { actionController, onSave, isEditOperation, ...otherProps } = props;

	const workflows: ComponentItem[] = [{ name: loc.resource(USE_TEMPLATE), id: USE_TEMPLATE }, { name: loc.resource(USE_CUSTOM), id: USE_CUSTOM }];
	const zIndex = getDialogZIndex();

	const [newTemplateForConfirmation, setNewTemplateForConfirmation] = useState<ComponentItem>(null);
	const [openTemplateConfirmationDialog, setOpenTemplateConfirmationDialog] = useState(false);

	const [newWorkflowForConfirmation, setNewWorkflowForConfirmation] = useState<ComponentItem>(null);
	const [openWorkflowConfirmationDialog, setOpenWorkflowConfirmationDialog] = useState(false);

	const document = actionController.getNode();
	const currentRevision = actionController.m_cache.getNode(document.CurrentRevision);

	const onWorkflowChange = (item) => {
		if (item.id == selectedWorkflow?.id) {
			return;
		} else if (item.id == USE_CUSTOM && !selectedTemplate) {
			store.setWorkflow(workflows[USE_CUSTOM_INDEX], isEditOperation);
		} else if (item.id == USE_TEMPLATE && !isCustomTemplateEdited()) {
			store.setWorkflow(workflows[USE_TEMPLATE_INDEX], isEditOperation);
		} else {
			setNewWorkflowForConfirmation(item);
			setOpenWorkflowConfirmationDialog(true);
		}
	};

	const onNewTemplateSelection = (item) => {
		if (store.selectedTemplate) {
			setNewTemplateForConfirmation(item);
			setOpenTemplateConfirmationDialog(true);
		} else {
			store.setTemplate(item);
		}
	};

	const executeProofOperation = async () => {
		if (!validateForm(isEditOperation)) {
			return false;
		} else {
			onSave(buildRequest(currentRevision, isEditOperation)
				.go());
			return true;
		}
	};

	useEffect(() => {
		if (isEditOperation) {
			store.setWorkflow(workflows[USE_CUSTOM_INDEX], isEditOperation);
			populateForm(currentRevision);
		} else {
			getPreviousDocRevisionWithProof(document)
				.then((data) => {
					if (data) {
						store.setWorkflow(workflows[USE_CUSTOM_INDEX], true);
						populateForm(data, true);
					} else {
						store.setWorkflow(workflows[USE_TEMPLATE_INDEX], isEditOperation);
					}
				});
		}
	}, []);

	function setProofOwner(event: SyntheticEvent, value: ComponentItem): void {
		store.setProofOwner(value.id);
	}

	return (
		<>
			<SaveDialog
				disableSave={hasErrorOnStage}
				onSave={executeProofOperation}
				style={{
					overflowY: "auto",
					zIndex: zIndex ?? "",
				}}
				titleRes={isEditOperation ? "EditProofWorkflow" : "StartProof"}
				{...otherProps}
			>
				{{
					main: (
						<RootBox>
							{formError && (
								<Message
									bulletPoints={formError.bulletPoints}
									sx={{ marginBottom: 24 }}
									titleRes={formError.titleRes}
									titleResParams={formError.titleResParams}
									titleText={formError.titleText}
									variant="error"
								/>
							)}
							<FlexColumn px={5}>
								<FlexColumn
									alignItems="center"
									width="100%"
								>
									<FlexRowCenter className={classes.textFieldContainer}>
										<FormControlLabel
											control={(
												<TextField
													autoFocus
													className={classes.textInput}
													disabled
													name="file"
													value={currentRevision["File.File"]}
												/>
											)}
											label={<Text bold res="DocumentRevision.Attr.File" />}
											labelPlacement="start"
										/>
									</FlexRowCenter>
									<FlexRowCenter className={classes.textFieldContainer} mt={6}>
										<FormControlLabel
											control={(
												<TextField
													autoFocus
													className={classes.textInput}
													disabled
													name="documentName"
													value={document.$Name}
												/>
											)}
											label={<Text bold res="Document.Name" />}
											labelPlacement="start"
										/>
									</FlexRowCenter>
									{!isEditOperation && (
										<FlexRowCenter className={classes.textFieldContainer} mt={6}>
											<FormControlLabel
												control={(
													<StyledSingleSelect
														name="workflow"
														onChange={(event, newValueItem, reason) => onWorkflowChange(newValueItem)}
														options={workflows}
														required
														value={selectedWorkflow}
													/>
												)}
												label={<Text bold res="Workflow.Name" />}
												labelPlacement="start"
											/>
											<ConfirmDialog
												category="warn"
												confirmRes="BUTTON_CONT"
												messageRes="WorkflowChangeMessage"
												onCancel={() => store.setWorkflow(workflows.find((w) => w.id == selectedWorkflow.id), false)}
												onClose={() => setOpenWorkflowConfirmationDialog(false)}
												onConfirm={() => store.setWorkflow(newWorkflowForConfirmation, isEditOperation)}
												open={openWorkflowConfirmationDialog}
												titleRes="WorkflowSelection"
											/>
										</FlexRowCenter>
									)}
									{selectedWorkflow?.id === USE_TEMPLATE && (
										<FlexRowCenter className={classes.textFieldContainer} mt={6}>
											<FormControlLabel
												control={(
													<StyledSingleSelect
														name="template"
														onChange={(event, newValueItem, reason) => onNewTemplateSelection(newValueItem)}
														options={activeTemplates}
														required
														value={selectedTemplate}
													/>
												)}
												label={<Text bold res="Template" />}
												labelPlacement="start"
											/>
											<ConfirmDialog
												category="warn"
												confirmRes="BUTTON_CONT"
												messageRes="WorkflowChangeMessage"
												onClose={() => setOpenTemplateConfirmationDialog(false)}
												onConfirm={() => store.setTemplate(newTemplateForConfirmation)}
												open={openTemplateConfirmationDialog}
												titleRes="TemplateSelection"
											/>
										</FlexRowCenter>
									)}
								</FlexColumn>
								{readyToShowStages && stages.map((stage, index) => (
									<ProofingStage
										key={index}
										{...stage}
										isEditOperation={isEditOperation}
										stageIndex={index + 1}
									/>
								))}
								{allowAddStage(isEditOperation) && (
									<>
										<FlexRowCenter my={6}>
											<Button
												design="outline"
												onClick={() => store.addStageWithOneReviewer()}
												startIcon={<AddIcon />}
											>
												<Text res="AddStage" />
											</Button>
										</FlexRowCenter>
										<FlexRowCenter>
											<Text
												bold
												res="ProofOwner"
												sx={{ color: black[100] }}
											/>
										</FlexRowCenter>
										<FlexRowCenter my={1}>
											<SingleSelect
												disabled={isEditOperation}
												onChange={setProofOwner}
												options={proofAdminUsers}
												required
												sx={{ width: 186 }}
												value={proofOwnerValue}
											/>
										</FlexRowCenter>
									</>
								)}
							</FlexColumn>
						</RootBox>
					),
				}}
			</SaveDialog>
			<PopupDialog
				open={isSelectProofOwnerDialogOpen}
				showClose
				titleRes="SelectProofOwner"
				width={540}
			>
				{{
					main: (
						<FlexColumn flexGrow={1}>
							<Text my={3} res="EditProofWorkflowSelectProofOwner" />
							<FlexRowCenter
								flexGrow={1}
								gap={4}
								my={3}
							>
								<Text bold res="ProofOwner" />
								<SingleSelect
									onChange={onChangeProofOwnerFromLegacy}
									options={proofAdminUsers}
									required
									sx={{ width: 400 }}
									value={proofAdminUsers[0]}
								/>
							</FlexRowCenter>
						</FlexColumn>
					),
					buttons: (
						<>
							<Button
								design="primary"
								onClick={onSaveSelectNewProofOwnerFromLegacy}
								res={ButtonEnum.BUTTON_CONTINUE}
							/>
							<Button
								design="link"
								onClick={onCloseSelectNewProofOwnerFromLegacy}
								res={ButtonEnum.BUTTON_CANCEL}
							/>
						</>
					),
				}}
			</PopupDialog>
		</>
	);
});

export default ProofOperationDialog;

